/**
 * Manages the list of configured object filters of an `ObjectFilterFormField`.
 * New filters are configured through a dialog, the resulting list is written
 * into a hidden input field when the form is submitted.
 *
 * @author Alexander Ebert
 * @copyright 2001-2026 WoltLab GmbH
 * @license GNU Lesser General Public License <http://opensource.org/licenses/lgpl-license.php>
 * @since 6.3
 */
define('WoltLabSuite/Core/Component/Object/Filter/Builder',["require", "exports", "WoltLabSuite/Core/Helper/PromiseMutex", "../../Confirmation", "../../Dialog", "WoltLabSuite/Core/Language"], function (require, exports, PromiseMutex_1, Confirmation_1, Dialog_1, Language_1) {
    "use strict";
    Object.defineProperty(exports, "__esModule", { value: true });
    exports.setup = setup;
    class ObjectFilterBuilder {
        #conditions = new Map();
        #container;
        #endpoint;
        constructor(container, endpoint, values) {
            this.#container = container;
            this.#endpoint = endpoint;
            const button = document.createElement("button");
            button.type = "button";
            button.classList.add("button");
            const icon = document.createElement("fa-icon");
            icon.setIcon("plus");
            button.appendChild(icon);
            const span = document.createElement("span");
            span.textContent = (0, Language_1.getPhrase)("wcf.objectFilter.addFilter");
            button.appendChild(span);
            button.addEventListener("click", (0, PromiseMutex_1.promiseMutex)(() => this.#addFilter()));
            this.#container.insertAdjacentElement("afterend", button);
            const form = this.#container.closest("form");
            let shadow = undefined;
            form?.addEventListener("submit", () => {
                if (shadow === undefined) {
                    shadow = document.createElement("input");
                    shadow.type = "hidden";
                    shadow.name = this.#container.id;
                    this.#container.insertAdjacentElement("afterend", shadow);
                }
                shadow.value = this.#serializeConditions();
            });
            this.#fromSerializedData(values);
        }
        /**
         * Restores the filters that were configured before.
         */
        #fromSerializedData(values) {
            for (const filter of values) {
                this.#createCondition(filter);
            }
        }
        /**
         * Returns the URL of the dialog, passing the identifiers of the configured
         * filters except for the given one that is being edited. Filters that are
         * not repeatable are no longer offered once they have been configured.
         */
        #getEndpoint(editedItem) {
            const url = new URL(this.#endpoint, window.location.href);
            this.#conditions.forEach((condition, item) => {
                if (item !== editedItem) {
                    url.searchParams.append("used[]", condition.identifier);
                }
            });
            return url;
        }
        /**
         * Opens the dialog to configure a new filter and adds it to the list.
         */
        async #addFilter() {
            const url = this.#getEndpoint();
            const response = await (0, Dialog_1.dialogFactory)().usingFormBuilder().fromEndpoint(url.toString());
            if (response.ok) {
                this.#createCondition(response.result);
            }
        }
        /**
         * Opens the dialog prefilled with the given filter and replaces it with the result.
         */
        async #editFilter(item, summary) {
            const data = this.#conditions.get(item);
            const url = this.#getEndpoint(item);
            url.searchParams.set("filter", data.identifier);
            url.searchParams.set("value", data.value);
            const response = await (0, Dialog_1.dialogFactory)().usingFormBuilder().fromEndpoint(url.toString());
            if (response.ok) {
                summary.innerHTML = response.result.summary;
                this.#conditions.set(item, response.result);
            }
        }
        /**
         * Adds the given filter to the list, showing its summary, a button to edit it
         * and a button to remove it after a confirmation.
         */
        #createCondition(data) {
            const item = document.createElement("div");
            item.classList.add("objectFilter__item");
            const summary = document.createElement("span");
            summary.innerHTML = data.summary;
            const buttons = document.createElement("div");
            buttons.classList.add("objectFilter__item__buttons");
            const editButton = document.createElement("button");
            editButton.type = "button";
            editButton.classList.add("button", "small", "jsTooltip");
            editButton.title = (0, Language_1.getPhrase)("wcf.global.button.edit");
            editButton.innerHTML = '<fa-icon name="pencil"></fa-icon>';
            editButton.addEventListener("click", (0, PromiseMutex_1.promiseMutex)(() => this.#editFilter(item, summary)));
            const deleteButton = document.createElement("button");
            deleteButton.type = "button";
            deleteButton.classList.add("button", "small", "jsTooltip");
            deleteButton.title = (0, Language_1.getPhrase)("wcf.global.button.delete");
            deleteButton.innerHTML = '<fa-icon name="times"></fa-icon>';
            deleteButton.addEventListener("click", (0, PromiseMutex_1.promiseMutex)(async () => {
                // Read the title when clicked, the summary changes when the filter is edited.
                if (await (0, Confirmation_1.confirmationFactory)().delete(summary.textContent)) {
                    this.#deleteCondition(item);
                }
            }));
            buttons.append(editButton, deleteButton);
            item.append(summary, buttons);
            if (this.#conditions.size > 0) {
                this.#container.append(this.#createJoiner());
            }
            this.#container.append(item);
            this.#conditions.set(item, data);
        }
        /**
         * Creates the element that is shown between two filters, all filters must be satisfied.
         */
        #createJoiner() {
            const joiner = document.createElement("div");
            joiner.classList.add("objectFilter__joiner");
            joiner.textContent = (0, Language_1.getPhrase)("wcf.objectFilter.joiner");
            return joiner;
        }
        /**
         * Removes the given filter from the list together with the joiner that
         * separates it from the previous filter, or from the next filter if it is
         * the first one.
         */
        #deleteCondition(element) {
            const joiner = element.previousElementSibling ?? element.nextElementSibling;
            if (joiner?.classList.contains("objectFilter__joiner")) {
                joiner.remove();
            }
            element.remove();
            this.#conditions.delete(element);
        }
        /**
         * Serializes the configured filters as a JSON-encoded list of
         * `[filterIdentifier, serializedValue]` pairs.
         */
        #serializeConditions() {
            const values = [];
            this.#conditions.forEach((condition) => {
                values.push([condition.identifier, condition.value]);
            });
            return JSON.stringify(values);
        }
    }
    /**
     * Initializes the filter builder for the given container.
     *
     * @param container element that holds the list of configured filters, its id is used as the name of the submitted value
     * @param endpoint URL of the dialog to configure a new filter
     * @param values filters that were configured before
     */
    function setup(container, endpoint, values) {
        new ObjectFilterBuilder(container, endpoint, values);
    }
});

